import { useMemo, useState } from 'react'
import { ArrowRightLeft, Pencil, Plus, Trash2, UserPlus } from 'lucide-react'

import type { Lead, LeadSource, LeadStatus } from '@/types/marketing'
import {
  CAPTURE_CHANNEL_LABELS,
  CAPTURE_CHANNEL_VARIANT,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
  LEAD_STATUS_VARIANT,
} from '@/types/marketing'
import {
  useConvertLead,
  useCreateLead,
  useDeleteLead,
  useLeads,
  useUpdateLead,
} from '@/features/marketing/hooks'
import { useCustomers } from '@/features/customers/hooks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'

const SOURCE_OPTIONS = Object.entries(LEAD_SOURCE_LABELS) as [LeadSource, string][]
const STATUS_OPTIONS = Object.entries(LEAD_STATUS_LABELS) as [LeadStatus, string][]
// "Converted" only arrives via the Convert action (it creates the customer
// link), so the edit dialog never offers it by hand.
const EDITABLE_STATUS_OPTIONS = STATUS_OPTIONS.filter(([value]) => value !== 'converted')

const sourceLabel = (s: string) =>
  s === '' ? 'Unknown' : (LEAD_SOURCE_LABELS[s as LeadSource] ?? s)

export function leadName(lead: Pick<Lead, 'first_name' | 'last_name' | 'company_name'>) {
  const name = `${lead.first_name} ${lead.last_name}`.trim()
  return name || lead.company_name || 'Unnamed'
}

export function LeadsTab() {
  const { data: leads = [], isLoading } = useLeads()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Lead | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')

  const filtered = useMemo(
    () =>
      leads.filter(
        (l) =>
          (statusFilter === 'all' || l.status === statusFilter) &&
          (sourceFilter === 'all' || l.source === sourceFilter),
      ),
    [leads, statusFilter, sourceFilter],
  )

  const openLeads = leads.filter((l) => l.status === 'new').length
  const converted = leads.filter((l) => l.status === 'converted').length
  const conversionRate = leads.length > 0 ? Math.round((converted / leads.length) * 100) : 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {leads.length} prospects captured · {openLeads} new · {converted} converted (=
          {conversionRate}%)
        </p>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
              {SOURCE_OPTIONS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => {
              setEditing(null)
              setDialogOpen(true)
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> Add Lead
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {leads.length === 0
            ? 'No leads yet — add one manually or connect Facebook Lead Ads / the lead webhook in Settings → Account.'
            : 'No leads match the current filters.'}
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prospect</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Captured via</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell>
                    <div className="font-medium">{leadName(lead)}</div>
                    <div className="text-xs text-muted-foreground">
                      {[lead.email, lead.phone].filter(Boolean).join(' · ') || 'no contact info'}
                    </div>
                  </TableCell>
                  <TableCell>{sourceLabel(lead.source)}</TableCell>
                  <TableCell>
                    <Badge variant={CAPTURE_CHANNEL_VARIANT[lead.channel]}>
                      {CAPTURE_CHANNEL_LABELS[lead.channel]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={LEAD_STATUS_VARIANT[lead.status]}>
                      {LEAD_STATUS_LABELS[lead.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>{lead.assigned_to || '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {lead.status !== 'converted' && lead.status !== 'lost' && (
                        <ConvertButton lead={lead} />
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditing(lead)
                          setDialogOpen(true)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <LeadDeleteButton lead={lead} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <LeadDialog open={dialogOpen} onOpenChange={setDialogOpen} lead={editing} />
    </div>
  )
}

function ConvertButton({ lead }: { lead: Lead }) {
  const convertMutation = useConvertLead()
  const [confirmOpen, setConfirmOpen] = useState(false)

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        title="Convert to customer"
        onClick={() => setConfirmOpen(true)}
      >
        <ArrowRightLeft className="h-4 w-4" />
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" /> Convert {leadName(lead)} to a customer?
            </DialogTitle>
            <DialogDescription>
              A customer record is created with this lead's contact details, source, and referrer —
              from there Sales owns the relationship and nurture sequences can enroll them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={convertMutation.isPending}
              onClick={async () => {
                await convertMutation.mutateAsync(lead.id)
                setConfirmOpen(false)
              }}
            >
              {convertMutation.isPending ? 'Converting…' : 'Convert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function LeadDeleteButton({ lead }: { lead: Lead }) {
  const deleteMutation = useDeleteLead()
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setConfirmOpen(true)}>
        <Trash2 className="h-4 w-4" />
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete lead?</DialogTitle>
            <DialogDescription>
              “{leadName(lead)}” is removed from the intake. Customers already converted from a
              lead are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                await deleteMutation.mutateAsync(lead.id)
                setConfirmOpen(false)
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface LeadFormState {
  first_name: string
  last_name: string
  company_name: string
  email: string
  phone: string
  source: string
  status: string
  assigned_to: string
  referrer_id: string
  notes: string
}

const EMPTY_FORM: LeadFormState = {
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
  source: '',
  status: 'new',
  assigned_to: '',
  referrer_id: '',
  notes: '',
}

function LeadDialog({
  open,
  onOpenChange,
  lead,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  lead: Lead | null
}) {
  const createMutation = useCreateLead()
  const updateMutation = useUpdateLead()
  const pending = createMutation.isPending || updateMutation.isPending
  const { data: customers = [] } = useCustomers()

  const [form, setForm] = useState<LeadFormState>(EMPTY_FORM)
  const set = <K extends keyof LeadFormState>(key: K, value: LeadFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  // Reset the form whenever the dialog opens or the target lead changes
  // ("adjust state during render" idiom, same as the segment dialog).
  const formKey = `${open}:${lead?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open) {
      setForm(
        lead
          ? {
              first_name: lead.first_name,
              last_name: lead.last_name,
              company_name: lead.company_name ?? '',
              email: lead.email,
              phone: lead.phone,
              source: lead.source || 'unknown',
              status: lead.status,
              assigned_to: lead.assigned_to,
              referrer_id: lead.referrer_id || 'none',
              notes: lead.notes,
            }
          : { ...EMPTY_FORM, source: 'unknown', referrer_id: 'none' },
      )
    }
  }

  const canSave = form.first_name || form.last_name || form.company_name || form.email || form.phone

  const submit = async () => {
    const body = {
      first_name: form.first_name,
      last_name: form.last_name,
      company_name: form.company_name,
      email: form.email,
      phone: form.phone,
      // Select sentinels map back to "not set" for the API.
      source: form.source === 'unknown' ? '' : form.source,
      referrer_id: form.referrer_id === 'none' ? '' : form.referrer_id,
      status: form.status,
      notes: form.notes,
      assigned_to: form.assigned_to,
    }
    try {
      if (lead) {
        await updateMutation.mutateAsync({ id: lead.id, body })
      } else {
        await createMutation.mutateAsync(body)
      }
      onOpenChange(false)
    } catch {
      // keep the dialog open; the global error toast shows the message
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{lead ? 'Edit lead' : 'Add lead'}</DialogTitle>
          <DialogDescription>
            A prospect captured before they become a customer. Convert when they're ready to buy.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Input
              placeholder="First name"
              value={form.first_name}
              onChange={(e) => set('first_name', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Input
              placeholder="Last name"
              value={form.last_name}
              onChange={(e) => set('last_name', e.target.value)}
            />
          </div>
          <Input
            placeholder="Company"
            className="col-span-2"
            value={form.company_name}
            onChange={(e) => set('company_name', e.target.value)}
          />
          <Input
            placeholder="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          <Input
            placeholder="Phone"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
          <Select value={form.source} onValueChange={(v) => set('source', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unknown">Unknown</SelectItem>
              {SOURCE_OPTIONS.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {lead && (
            <Select value={form.status} onValueChange={(v) => set('status', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {(lead.status === 'converted' ? STATUS_OPTIONS : EDITABLE_STATUS_OPTIONS).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          )}
          <Input
            placeholder="Assigned to"
            value={form.assigned_to}
            onChange={(e) => set('assigned_to', e.target.value)}
          />
          <Select value={form.referrer_id || 'none'} onValueChange={(v) => set('referrer_id', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Referred by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No referrer</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {`${c.first_name} ${c.last_name}`.trim() || c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Notes (custom form answers, context, …)"
            className="col-span-2"
            rows={3}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSave || pending} onClick={submit}>
            {pending ? 'Saving…' : lead ? 'Save' : 'Add lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
